import { StructuredDescription } from "@/models"

import type { JSONContent } from "@tiptap/vue-3"

export interface AcceptanceCriteriaChecklistState {
  present: boolean
  complete: boolean
}

/**
 * Reads acceptance-criteria checklist state from editor HTML so the automatic DoD mirrors it immediately.
 */
export function getAcceptanceCriteriaChecklistState(
  acceptanceCriteria?: StructuredDescription | null
): AcceptanceCriteriaChecklistState {
  const html = acceptanceCriteria?.html ?? ""
  if (!html) return { present: false, complete: false }

  const document = new DOMParser().parseFromString(html, "text/html")
  const checkboxes = Array.from(
    document.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
  )
  if (!checkboxes.length) return { present: false, complete: false }

  return {
    present: true,
    complete: checkboxes.every(checkbox => {
      const checklistItem = checkbox.closest<HTMLElement>('[data-type="taskItem"]')
      return (
        checkbox.checked ||
        checkbox.hasAttribute("checked") ||
        checklistItem?.dataset.checked === "true"
      )
    })
  }
}

/** Marks every acceptance-criteria checklist item as completed in both editor representations. */
export function completeAcceptanceCriteriaChecklist(
  acceptanceCriteria?: StructuredDescription | null
): StructuredDescription | undefined {
  if (!acceptanceCriteria) return undefined

  const document = new DOMParser().parseFromString(acceptanceCriteria.html ?? "", "text/html")
  document.querySelectorAll<HTMLElement>('li[data-type="taskItem"]').forEach(item => {
    item.dataset.checked = "true"
    const checkbox = item.querySelector<HTMLInputElement>('input[type="checkbox"]')
    if (checkbox) {
      checkbox.checked = true
      checkbox.setAttribute("checked", "checked")
    }
  })

  let json = acceptanceCriteria.json
  if (json) {
    const content = typeof json === "string" ? JSON.parse(json) : json
    const completeTaskItems = (node: JSONContent) => {
      if (node?.type === "taskItem") {
        node.attrs = { ...node.attrs, checked: true }
      }
      node?.content?.forEach(completeTaskItems)
    }
    completeTaskItems(content)
    json = JSON.stringify(content)
  }

  return new StructuredDescription({
    ...acceptanceCriteria,
    html: document.body.innerHTML,
    json
  })
}
